import React, { useState } from "react";

import { getPhoneConfigForIsd } from "constants/phoneConfig";
import {
  getPhoneValidationError,
  REQUIRED_ISD_ERROR,
  sanitizePhone,
} from "utils/phoneValidation";
import { useCountryIsdList } from "services/mdsService";
import ErrorMessage from "widgets/ErrorMessage/ErrorMessage";
import SingleSelect from "widgets/Dropdown/SingleSelect";
import Textfield from "widgets/Textfield/Textfield";

import styles from "./PhoneWithIsdField.module.scss";

export const COUNTRY_LIST_ERROR_MESSAGE =
  "Failed to load country list. Please refresh the page.";

const PhoneWithIsdField = ({
  id,
  label,
  isdCode,
  phone,
  onIsdCodeChange,
  onPhoneChange,
  onPhoneBlur,
  error: externalError,
  disabled = false,
  lockIsd = false,
  className = "",
}) => {
  const [phoneTouched, setPhoneTouched] = useState(false);

  const {
    data: isdMenuItems = [],
    isLoading: isLoadingCountries,
    isError: isCountryListError,
  } = useCountryIsdList();

  const maxPhoneLength = getPhoneConfigForIsd(isdCode).maxLength;
  const selectedIsdValue = isdMenuItems.some((item) => item.value === isdCode)
    ? isdCode
    : "";

  const shouldShowValidation =
    phoneTouched || Boolean(sanitizePhone(phone));
  const phoneError = getPhoneValidationError(phone, isdCode);
  const internalError =
    shouldShowValidation && phoneError !== REQUIRED_ISD_ERROR ? phoneError : null;
  const displayError =
    externalError !== undefined
      ? externalError && externalError !== REQUIRED_ISD_ERROR
        ? externalError
        : null
      : internalError;

  const markPhoneTouched = (event) => {
    setPhoneTouched(true);
    onPhoneBlur?.(event);
  };

  const handleIsdChange = (event) => {
    const nextIsdCode = event.target.value;
    const nextMaxLength = getPhoneConfigForIsd(nextIsdCode).maxLength;
    onIsdCodeChange?.(nextIsdCode);
    onPhoneChange(phone.slice(0, nextMaxLength));
    setPhoneTouched(true);
  };

  const handlePhoneChange = (event) => {
    onPhoneChange(
      event.target.value.replace(/\D/g, "").slice(0, maxPhoneLength),
    );
  };

  const isIsdDisabled =
    disabled || isLoadingCountries || isCountryListError;
  const showCountryListError = !lockIsd && isCountryListError;

  return (
    <div className={`${styles.phoneWithIsdField} ${className}`.trim()}>
      <div className={styles.isdSelector}>
        {lockIsd ? (
          <div className={styles.isdLockedValue}>{isdCode || "—"}</div>
        ) : (
          <SingleSelect
            id={`${id}-isd`}
            tag="ISD"
            value={selectedIsdValue}
            menuItems={isdMenuItems}
            disabled={isIsdDisabled}
            handleChange={handleIsdChange}
          />
        )}
      </div>
      <div className={styles.phoneInputColumn}>
        <div className={styles.phoneInput}>
          <Textfield
            id={id}
            label={label}
            type="text"
            value={phone}
            handleOnChange={handlePhoneChange}
            onBlur={markPhoneTouched}
            disabled={disabled}
          />
        </div>
        {showCountryListError && (
          <div className={styles.phoneError}>
            <ErrorMessage>{COUNTRY_LIST_ERROR_MESSAGE}</ErrorMessage>
          </div>
        )}
        {!showCountryListError && displayError && (
          <div className={styles.phoneError}>
            <ErrorMessage>{displayError}</ErrorMessage>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhoneWithIsdField;
